/**
 * useBusinessClaims - React Query hooks for business claim operations
 * SESSION_329: Non-Existent Business Claim Workflow
 *
 * Provides hooks for:
 * - Submitting business claims (mutation)
 * - Submitting new business claims (mutation) - for non-existent businesses
 * - Verifying claim codes (mutation)
 * - Fetching user's claims (query)
 * - Fetching pending claims for admin (query)
 * - Approving/rejecting claims (mutations)
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult
} from '@tanstack/react-query';
import {
  businessClaimApi,
  type GetPendingBusinessClaimsParams
} from '@/infrastructure/api/BusinessClaimApi';
import { notificationService } from '@/infrastructure/notifications';
import type {
  BusinessClaimsPagedResponse,
  UserClaimsResponse,
  SubmitBusinessClaimCommand,
  SubmitNewBusinessClaimCommand,
  SubmitClaimResponse,
  VerifyClaimCodeCommand,
  VerifyClaimCodeResponse,
  ApproveBusinessClaimCommand,
  ApproveNewBusinessClaimCommand,
  RejectBusinessClaimCommand,
  AdminClaimActionResponse
} from '@/domain/entities/BusinessClaim';

/**
 * Query keys for business claims
 * Used for cache invalidation and refetching
 */
export const businessClaimKeys = {
  all: ['businessClaims'] as const,
  lists: () => [...businessClaimKeys.all, 'list'] as const,
  myList: () => [...businessClaimKeys.lists(), 'my'] as const,
  pendingList: (params?: GetPendingBusinessClaimsParams) =>
    [...businessClaimKeys.lists(), 'pending', params] as const,
  // SESSION_349: Rejected Claim Management
  rejectedList: (params?: GetPendingBusinessClaimsParams) =>
    [...businessClaimKeys.lists(), 'rejected', params] as const,
  details: () => [...businessClaimKeys.all, 'detail'] as const,
  detail: (id: string) => [...businessClaimKeys.details(), id] as const
};

/**
 * useMyBusinessClaims - Query hook for fetching current user's business claims
 *
 * @param options - Query options (e.g., { enabled: boolean })
 * @returns React Query result with user's business claims
 */
export function useMyBusinessClaims(options?: {
  enabled?: boolean;
}): UseQueryResult<UserClaimsResponse, Error> {
  return useQuery<UserClaimsResponse, Error>({
    queryKey: businessClaimKeys.myList(),
    queryFn: () => businessClaimApi.getMyClaims(),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
    retry: 2,
    retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
}

/**
 * usePendingBusinessClaims - Query hook for fetching pending claims (admin)
 *
 * @param params - Pagination parameters
 * @param options - Query options
 * @returns React Query result with pending business claims
 */
export function usePendingBusinessClaims(
  params?: GetPendingBusinessClaimsParams,
  options?: { enabled?: boolean }
): UseQueryResult<BusinessClaimsPagedResponse, Error> {
  return useQuery<BusinessClaimsPagedResponse, Error>({
    queryKey: businessClaimKeys.pendingList(params),
    queryFn: () => businessClaimApi.getPendingClaims(params),
    enabled: options?.enabled ?? true,
    staleTime: 1 * 60 * 1000, // 1 minute - admin queue should be fresh
    refetchOnWindowFocus: true,
    retry: 2,
    retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
}

/**
 * useRejectedBusinessClaims - Query hook for fetching rejected claims (admin)
 * SESSION_349: Rejected Claim Management
 *
 * Allows admins to view previously rejected claims for potential re-approval.
 *
 * @param params - Pagination parameters
 * @param options - Query options
 * @returns React Query result with rejected business claims
 */
export function useRejectedBusinessClaims(
  params?: GetPendingBusinessClaimsParams,
  options?: { enabled?: boolean }
): UseQueryResult<BusinessClaimsPagedResponse, Error> {
  return useQuery<BusinessClaimsPagedResponse, Error>({
    queryKey: businessClaimKeys.rejectedList(params),
    queryFn: () => businessClaimApi.getRejectedClaims(params),
    enabled: options?.enabled ?? true,
    staleTime: 1 * 60 * 1000, // 1 minute - admin queue should be fresh
    refetchOnWindowFocus: true,
    retry: 2,
    retryDelay: (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 30000)
  });
}

/**
 * useSubmitBusinessClaim - Mutation hook for submitting a business claim
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useSubmitBusinessClaim(): UseMutationResult<
  SubmitClaimResponse,
  Error,
  SubmitBusinessClaimCommand
> {
  const queryClient = useQueryClient();

  return useMutation<SubmitClaimResponse, Error, SubmitBusinessClaimCommand>({
    mutationFn: (command: SubmitBusinessClaimCommand) => businessClaimApi.submitClaim(command),
    onSuccess: (response: SubmitClaimResponse) => {
      // Invalidate user claims list
      void queryClient.invalidateQueries({
        queryKey: businessClaimKeys.myList()
      });

      if (response.success) {
        notificationService.showSuccess(response.message || 'Business claim submitted successfully');
      }
    },
    onError: (error: Error) => {
      console.error('Failed to submit business claim:', error);
    }
  });
}

/**
 * useSubmitNewBusinessClaim - Mutation hook for submitting a new business claim
 * SESSION_328: Non-Existent Business Claim Workflow
 *
 * Used when user wants to claim a business that doesn't exist in the database.
 * The claim goes directly to admin review for business creation.
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useSubmitNewBusinessClaim(): UseMutationResult<
  SubmitClaimResponse,
  Error,
  SubmitNewBusinessClaimCommand
> {
  const queryClient = useQueryClient();

  return useMutation<SubmitClaimResponse, Error, SubmitNewBusinessClaimCommand>({
    mutationFn: (command: SubmitNewBusinessClaimCommand) =>
      businessClaimApi.submitNewBusinessClaim(command),
    onSuccess: (response: SubmitClaimResponse) => {
      // Invalidate user claims list
      void queryClient.invalidateQueries({
        queryKey: businessClaimKeys.myList()
      });

      if (response.success) {
        notificationService.showSuccess(
          response.message || 'Business request submitted for review'
        );
      }
    },
    onError: (error: Error) => {
      console.error('Failed to submit new business claim:', error);
    }
  });
}

/**
 * useVerifyBusinessClaimCode - Mutation hook for verifying claim code
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useVerifyBusinessClaimCode(): UseMutationResult<
  VerifyClaimCodeResponse,
  Error,
  { claimId: string; command: VerifyClaimCodeCommand }
> {
  const queryClient = useQueryClient();

  return useMutation<
    VerifyClaimCodeResponse,
    Error,
    { claimId: string; command: VerifyClaimCodeCommand }
  >({
    mutationFn: ({ claimId, command }) => businessClaimApi.verifyClaimCode(claimId, command),
    onSuccess: (response: VerifyClaimCodeResponse) => {
      // Invalidate user claims list
      void queryClient.invalidateQueries({
        queryKey: businessClaimKeys.myList()
      });

      if (response.success) {
        notificationService.showSuccess(response.message || 'Verification successful');
      }
    },
    onError: (error: Error) => {
      console.error('Failed to verify business claim code:', error);
    }
  });
}

/**
 * useApproveBusinessClaim - Mutation hook for approving an existing business claim (admin)
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useApproveBusinessClaim(): UseMutationResult<
  AdminClaimActionResponse,
  Error,
  { claimId: string; command?: ApproveBusinessClaimCommand }
> {
  const queryClient = useQueryClient();

  return useMutation<
    AdminClaimActionResponse,
    Error,
    { claimId: string; command?: ApproveBusinessClaimCommand }
  >({
    mutationFn: ({ claimId, command }) => businessClaimApi.approveClaim(claimId, command),
    onSuccess: (response: AdminClaimActionResponse) => {
      // Invalidate pending claims list
      void queryClient.invalidateQueries({
        queryKey: businessClaimKeys.lists()
      });

      if (response.success) {
        notificationService.showSuccess(response.message || 'Business claim approved');
      }
    },
    onError: (error: Error) => {
      console.error('Failed to approve business claim:', error);
    }
  });
}

/**
 * useApproveNewBusinessClaim - Mutation hook for approving a new business claim (admin)
 * SESSION_328: Non-Existent Business Claim Workflow
 *
 * Creates the business and approves the claim in one operation.
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useApproveNewBusinessClaim(): UseMutationResult<
  AdminClaimActionResponse,
  Error,
  { claimId: string; command: ApproveNewBusinessClaimCommand }
> {
  const queryClient = useQueryClient();

  return useMutation<
    AdminClaimActionResponse,
    Error,
    { claimId: string; command: ApproveNewBusinessClaimCommand }
  >({
    mutationFn: ({ claimId, command }) =>
      businessClaimApi.approveNewBusinessClaim(claimId, command),
    onSuccess: (response: AdminClaimActionResponse) => {
      // Invalidate pending claims list
      void queryClient.invalidateQueries({
        queryKey: businessClaimKeys.lists()
      });

      if (response.success) {
        notificationService.showSuccess(
          response.message || 'Business created and claim approved'
        );
      }
    },
    onError: (error: Error) => {
      console.error('Failed to approve new business claim:', error);
    }
  });
}

/**
 * useRejectBusinessClaim - Mutation hook for rejecting a business claim (admin)
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export function useRejectBusinessClaim(): UseMutationResult<
  AdminClaimActionResponse,
  Error,
  { claimId: string; command: RejectBusinessClaimCommand }
> {
  const queryClient = useQueryClient();

  return useMutation<
    AdminClaimActionResponse,
    Error,
    { claimId: string; command: RejectBusinessClaimCommand }
  >({
    mutationFn: ({ claimId, command }) => businessClaimApi.rejectClaim(claimId, command),
    onSuccess: (response: AdminClaimActionResponse) => {
      // Invalidate pending claims list
      void queryClient.invalidateQueries({
        queryKey: businessClaimKeys.lists()
      });

      if (response.success) {
        notificationService.showSuccess(response.message || 'Business claim rejected');
      }
    },
    onError: (error: Error) => {
      console.error('Failed to reject business claim:', error);
    }
  });
}
